/**
 * Iframe bridge — postMessage protocol with the Rumio parent frame.
 *
 * Contract (parent side: rumio repo, web/src/components/AppEmbed.tsx):
 *   App → Parent: { type: "rumio:height", height }       // auto-resize
 *   App → Parent: { type: "rumio:location", path }       // internal nav
 *   App → Parent: { type: "rumio:session:refresh" }      // request fresh JWT
 *   App → Parent: { type: "rumio:help", article }        // open a help article (contract v1.23)
 *   Parent → App: { type: "rumio:navigate", path }       // change app's route
 *   Parent → App: { type: "rumio:session", token }       // refresh response
 *
 * Inbound messages are honored only from the parent frame itself (any other
 * window — a popup, a nested ad frame — can postMessage us too). Outbound ones
 * never carry the session token.
 */

export function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin throw means we're in an iframe whose parent we can't read.
    return true;
  }
}

/** True when a message event came from our parent frame. */
export function isFromParent(ev: MessageEvent): boolean {
  return typeof window !== "undefined" && window.parent !== window && ev.source === window.parent;
}

function postToParent(message: unknown): void {
  if (typeof window === "undefined") return;
  if (!window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(message, "*");
  } catch {
    // Cross-origin posts can't fail here — postMessage is fire-and-forget.
  }
}

export function sendHeight(height: number): void {
  postToParent({ type: "rumio:height", height });
}

export function sendLocation(path: string): void {
  postToParent({ type: "rumio:location", path: withoutSessionParam(path) });
}

/** Strip `rumio_session` from a path+query so the JWT never leaves the frame. */
export function withoutSessionParam(path: string): string {
  const [pathAndQuery, hash = ""] = path.split("#", 2);
  const [p, q = ""] = pathAndQuery.split("?", 2);
  const params = new URLSearchParams(q);
  params.delete("rumio_session");
  const qs = params.toString();
  return `${p}${qs ? `?${qs}` : ""}${hash ? `#${hash}` : ""}`;
}

/** Ask the host site to open one of its help articles in its own widget. */
export function sendHelpRequest(article: string): void {
  postToParent({ type: "rumio:help", article });
}

export function requestSessionRefresh(): void {
  postToParent({ type: "rumio:session:refresh" });
}

/**
 * Fire `rumio:height` on every layout-changing event. Returns the
 * disposer; call it on unmount.
 */
export function startHeightSync(): () => void {
  if (typeof window === "undefined") return () => {};
  const send = () => sendHeight(document.documentElement.scrollHeight);
  send();
  const observer = new ResizeObserver(send);
  observer.observe(document.body);
  window.addEventListener("load", send);
  return () => {
    observer.disconnect();
    window.removeEventListener("load", send);
  };
}

/**
 * Listen for parent-initiated navigation. Returns the disposer.
 *
 * The handler is invoked with the requested path; pass it to React Router's
 * `navigate()` (or push the URL manually) so the iframe URL changes.
 */
export function listenForNavigation(handler: (path: string) => void): () => void {
  if (typeof window === "undefined") return () => {};
  function onMessage(ev: MessageEvent) {
    if (!isFromParent(ev)) return;
    const data = ev.data as any;
    if (!data || typeof data !== "object" || data.type !== "rumio:navigate") return;
    if (typeof data.path === "string") handler(data.path);
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
